import { useState } from "react"
import {
  CheckCircle,
  Clock,
  Users,
  Zap,
  Calendar,
  Building2,
  Loader2,
} from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "sonner"
import { API_URL } from "@/lib/env"
import type { BookDemoFormData, DemoBenefit } from "./types"

const benefits: DemoBenefit[] = [
  {
    id: "personalized",
    title: "Personalized Demo",
    description: "See AI employees tailored to your industry and use cases",
  },
  {
    id: "roi",
    title: "ROI Analysis",
    description: "Get a custom cost-benefit analysis for your organization",
  },
  {
    id: "integration",
    title: "Integration Review",
    description: "Learn how Taqat integrates with your existing systems",
  },
  {
    id: "pricing",
    title: "Custom Pricing",
    description: "Receive enterprise pricing based on your requirements",
  },
]

const initialFormData: BookDemoFormData = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  jobTitle: "",
  phone: "",
  message: "",
}

/**
 * Book Demo section with a lead capture form for enterprise customers.
 * Displays benefits on the left and a form on the right.
 */
export function BookDemoSection() {
  const [formData, setFormData] = useState<BookDemoFormData>(initialFormData)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  /**
   * Handles form field changes by updating the form state.
   */
  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  /**
   * Handles form submission by calling the book-demo API endpoint.
   */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      const response = await fetch(`${API_URL}/public/book-demo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      })

      const result = await response.json()

      if (response.ok && result.success) {
        setIsSubmitted(true)
        toast.success("Demo request submitted successfully!")
      } else {
        const errorMessage = result.error?.message || "Failed to submit demo request"
        toast.error(errorMessage)
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="relative overflow-hidden bg-neutral-900 pt-24 pb-16 lg:pt-32 lg:pb-24">
      {/* Background decoration */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Left Column - Info */}
          <div className="flex flex-col justify-center">
            <Badge className="mb-6 w-fit border-primary/30 bg-primary/10 text-primary">
              Get Started
            </Badge>
            <h2 className="mb-6 text-3xl font-bold text-white lg:text-4xl">
              Book a <span className="text-primary">Personalized Demo</span>
            </h2>
            <p className="mb-10 text-lg text-neutral-400">
              See how Taqat AI employees can transform your organization.
              Schedule a 30-minute demo with our team to explore solutions
              tailored to your needs.
            </p>

            {/* Benefits */}
            <div className="space-y-5">
              {benefits.map((benefit) => (
                <div key={benefit.id} className="flex gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <CheckCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{benefit.title}</h3>
                    <p className="text-sm text-neutral-400">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust indicators */}
            <div className="mt-10 flex flex-wrap items-center gap-6 border-t border-neutral-800 pt-8">
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <Clock className="h-4 w-4 text-primary" />
                <span>30 min demo</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <Users className="h-4 w-4 text-primary" />
                <span>Expert guidance</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-400">
                <Zap className="h-4 w-4 text-primary" />
                <span>No commitment</span>
              </div>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="relative">
            <div className="rounded-2xl bg-white p-8 shadow-2xl">
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                    <Calendar className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-neutral-900">
                    Thank you for your interest!
                  </h3>
                  <p className="mb-6 text-neutral-600">
                    Our team will reach out within 24 hours to schedule your
                    personalized demo.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsSubmitted(false)
                      setFormData(initialFormData)
                    }}
                  >
                    Submit another request
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-neutral-900">
                      Request a Demo
                    </h3>
                    <p className="text-sm text-neutral-500">
                      Fill out the form and we'll be in touch soon
                    </p>
                  </div>

                  {/* Name Fields */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Doe"
                        required
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">Work Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      required
                    />
                  </div>

                  {/* Company & Job Title */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="company">Company *</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Acme Inc."
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="jobTitle">Job Title *</Label>
                      <Input
                        id="jobTitle"
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        placeholder="VP of Operations"
                        required
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number (Optional)</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+974 1234 5678"
                    />
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message">
                      How can we help? (Optional)
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your use case or any specific requirements..."
                      rows={3}
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    className="w-full bg-primary text-white hover:bg-primary/90"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Request Demo"
                    )}
                  </Button>

                  <p className="text-center text-xs text-neutral-500">
                    By submitting, you agree to our{" "}
                    <a href="/privacy" className="text-primary hover:underline">
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a href="/terms" className="text-primary hover:underline">
                      Terms of Service
                    </a>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
